import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, Typography} from '@mui/material';

import {DepartmentHealthDataFreshnessSkeleton} from './DepartmentHealthDataFreshnessSkeleton';
import {
  departmentHealthSectionCardSx,
  departmentHealthSectionSubtitleSx,
  departmentHealthSectionTitleSx,
} from './departmentHealthSectionStyles';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {dataFreshnessBand, orangeColors, scorePill} from '../../../../../layouts/theme/designSystem';
import type {
  DepartmentHealthConfidence,
  DepartmentHealthDataFreshness as DepartmentHealthDataFreshnessData,
  DepartmentHealthFreshnessBandKey,
} from '../../types/DepartmentHealthTypes';
import {DEPARTMENT_HEALTH_FRESHNESS_DISPLAY_BANDS} from '../../utils/DepartmentHealth/departmentHealthFreshnessDisplay';

export interface Props {
  dataFreshness?: DepartmentHealthDataFreshnessData;
  loading?: boolean;
}

const bandColors: Record<DepartmentHealthFreshnessBandKey, (typeof dataFreshnessBand)[keyof typeof dataFreshnessBand]> =
  {
    fresh: dataFreshnessBand.fresh,
    ageing: dataFreshnessBand.ageing,
    stale: dataFreshnessBand.stale,
    veryStale: dataFreshnessBand.veryStale,
  };

const confidenceChipColors: Record<
  DepartmentHealthConfidence,
  {bgcolor: string; color: string; borderColor: string}
> = {
  high: {bgcolor: scorePill.high.bgcolor, color: scorePill.high.color, borderColor: scorePill.high.strokeColor},
  medium: {bgcolor: scorePill.medium.bgcolor, color: scorePill.medium.color, borderColor: orangeColors[100]},
  low: {bgcolor: scorePill.low.bgcolor, color: scorePill.low.color, borderColor: scorePill.low.strokeColor},
};

export const DepartmentHealthDataFreshness = ({dataFreshness, loading = false}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.dataFreshness'});
  const {t: tConfidence} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.narrative.confidence',
  });

  const bands = useMemo(() => {
    if (!dataFreshness) {
      return null;
    }

    return DEPARTMENT_HEALTH_FRESHNESS_DISPLAY_BANDS.map((band) => ({
      key: band.key,
      labelKey: band.labelKey,
      count: dataFreshness[band.countKey],
      pct: dataFreshness[band.pctKey],
    }));
  }, [dataFreshness]);

  if (loading || !dataFreshness || !bands) {
    return <DepartmentHealthDataFreshnessSkeleton />;
  }

  const confidenceColors = confidenceChipColors[dataFreshness.confidence];
  const visibleBarBands = bands.filter((band) => band.pct > 0);

  return (
    <EmpionPaper sx={departmentHealthSectionCardSx} data-testid='department-health-data-freshness'>
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 0.5}}>
        <Box>
          <Box component='h2' sx={{...departmentHealthSectionTitleSx, mb: 0.25}}>
            <Box component='span' sx={{color: 'text.secondary', display: 'flex', alignItems: 'center'}}>
              <AccessTimeOutlinedIcon sx={{fontSize: 15}} />
            </Box>
            {t('title')}
          </Box>
          <Typography variant='body2' sx={departmentHealthSectionSubtitleSx}>
            {t('subtitle')}
          </Typography>
        </Box>

        <Box
          component='span'
          sx={{
            flexShrink: 0,
            mt: 0.25,
            px: 1.25,
            py: 0.375,
            borderRadius: '99px',
            fontSize: '11px',
            fontWeight: 600,
            border: '1px solid',
            bgcolor: confidenceColors.bgcolor,
            color: confidenceColors.color,
            borderColor: confidenceColors.borderColor,
          }}
        >
          {tConfidence(dataFreshness.confidence)}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          height: 11,
          borderRadius: '99px',
          overflow: 'hidden',
          mt: 1.75,
          mb: 1.25,
        }}
      >
        {visibleBarBands.map((band) => (
          <Box
            key={band.key}
            sx={{
              flex: band.pct,
              bgcolor: bandColors[band.key].bar,
              borderRadius: '3px',
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1,
          mb: 1.75,
        }}
      >
        {bands.map((band) => {
          const colors = bandColors[band.key];

          return (
            <Box
              key={band.key}
              sx={{
                px: 1.25,
                py: 1.125,
                borderRadius: '9px',
                border: '1px solid',
                bgcolor: colors.bgcolor,
                borderColor: colors.borderColor,
              }}
            >
              <Typography sx={{fontSize: '19px', fontWeight: 700, lineHeight: 1, color: colors.color}}>
                {band.count}
              </Typography>
              <Typography sx={{fontSize: '10px', fontWeight: 600, mt: 0.25, color: colors.color}}>
                {t(band.labelKey)}
              </Typography>
              <Typography sx={{fontSize: '10px', fontWeight: 600, color: colors.color}}>{band.pct}%</Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.125,
          alignItems: 'flex-start',
          px: 1.625,
          py: 1.25,
          borderRadius: '8px',
          bgcolor: confidenceColors.bgcolor,
          border: '0.5px solid',
          borderColor: confidenceColors.borderColor,
          color: confidenceColors.color,
        }}
      >
        <InfoOutlinedIcon sx={{fontSize: 14, flexShrink: 0, mt: 0.25}} />
        <Typography sx={{fontSize: '12.5px', lineHeight: 1.55}}>
          {t(`messages.${dataFreshness.confidence}`, {
            freshPct: dataFreshness.freshPct,
            stalePct: dataFreshness.outdatedPct,
          })}
        </Typography>
      </Box>
    </EmpionPaper>
  );
};
